import { useState, useEffect } from 'react';
import { Loader2, Rocket, CheckCircle2, XCircle, Clock, RefreshCw, Terminal } from 'lucide-react';

const WEBHOOK_URL = 'http://13.62.255.226:9090';
const DEPLOY_SECRET = 'wtfh-deploy-2026';

export function DeployTab() {
  const [deploying, setDeploying] = useState(false);
  const [result, setResult] = useState(null);
  const [history, setHistory] = useState([]);
  const [webhookStatus, setWebhookStatus] = useState(null);

  const checkWebhook = async () => {
    try {
      const resp = await fetch(`${WEBHOOK_URL}/deploy/health`, { signal: AbortSignal.timeout(5000) });
      const data = await resp.json();
      setWebhookStatus(data.status === 'ok' ? 'online' : 'error');
    } catch {
      setWebhookStatus('offline');
    }
  };

  const loadHistory = async () => {
    try {
      const resp = await fetch(`${WEBHOOK_URL}/deploy/status`, { signal: AbortSignal.timeout(5000) });
      const data = await resp.json();
      setHistory(data.history || []);
    } catch { /* webhook may be offline */ }
  };

  const triggerDeploy = async () => {
    setDeploying(true);
    setResult(null);
    try {
      const resp = await fetch(`${WEBHOOK_URL}/deploy`, {
        method: 'POST',
        headers: { 'X-Deploy-Secret': DEPLOY_SECRET },
        signal: AbortSignal.timeout(360000),
      });
      const data = await resp.json();
      setResult(data);
      await loadHistory();
    } catch (e) {
      setResult({ success: false, steps: [{ step: 'connection', success: false, output: e.message }] });
    } finally {
      setDeploying(false);
    }
  };

  useEffect(() => { checkWebhook(); loadHistory(); }, []);

  return (
    <div data-testid="deploy-tab" className="space-y-4">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h3 className="text-sm font-bold text-gray-900">Deploy to AWS</h3>
          <p className="text-[10px] text-gray-400">Push latest code from GitHub to production</p>
        </div>
        <div className="flex items-center gap-2">
          <span className={`text-[9px] font-bold px-2 py-0.5 rounded-full ${
            webhookStatus === 'online' ? 'bg-green-100 text-green-600' :
            webhookStatus === 'offline' ? 'bg-red-100 text-red-600' :
            'bg-gray-100 text-gray-400'
          }`}>
            {webhookStatus === 'online' ? 'Webhook Online' : webhookStatus === 'offline' ? 'Webhook Offline' : 'Checking...'}
          </span>
          <button onClick={checkWebhook} className="p-1 hover:bg-gray-100 rounded-lg">
            <RefreshCw size={12} className="text-gray-400" />
          </button>
        </div>
      </div>

      {/* Deploy Steps */}
      <div className="bg-gray-50 rounded-xl border border-gray-200 p-3">
        <p className="text-[9px] font-bold text-gray-500 uppercase tracking-wider mb-2">What this does</p>
        <div className="space-y-1 text-[11px] text-gray-600">
          <p className="flex items-center gap-2"><Terminal size={10} /> 1. git pull (fetch latest from GitHub)</p>
          <p className="flex items-center gap-2"><Terminal size={10} /> 2. docker-compose up -d --build (rebuild + restart)</p>
        </div>
        <p className="text-[9px] text-gray-400 mt-2">Prerequisite: Save to GitHub from Emergent first.</p>
      </div>

      {/* Deploy Button */}
      <button
        data-testid="deploy-aws-btn"
        onClick={triggerDeploy}
        disabled={deploying || webhookStatus === 'offline'}
        className="w-full py-3 rounded-xl text-sm font-bold flex items-center justify-center gap-2 transition-all disabled:opacity-50"
        style={{
          background: deploying ? '#6366f1' : '#4f46e5',
          color: 'white',
        }}
      >
        {deploying ? <Loader2 size={16} className="animate-spin" /> : <Rocket size={16} />}
        {deploying ? 'Deploying... (this takes 2-5 mins)' : 'Deploy to AWS'}
      </button>

      {webhookStatus === 'offline' && (
        <div className="bg-amber-50 border border-amber-200 rounded-xl p-3">
          <p className="text-xs text-amber-700 font-semibold">Webhook not reachable</p>
          <p className="text-[10px] text-amber-600 mt-1">
            SSH into EC2 and start the webhook: <code className="bg-amber-100 px-1 rounded">nohup python3 deploy_webhook.py &</code>
          </p>
          <p className="text-[10px] text-amber-600 mt-1">
            Also ensure EC2 Security Group allows inbound port 9090.
          </p>
        </div>
      )}

      {/* Result */}
      {result && (
        <div className={`rounded-xl border p-3 ${result.success ? 'bg-green-50 border-green-200' : 'bg-red-50 border-red-200'}`}>
          <div className="flex items-center gap-2 mb-2">
            {result.success ? <CheckCircle2 size={14} className="text-green-600" /> : <XCircle size={14} className="text-red-600" />}
            <span className={`text-xs font-bold ${result.success ? 'text-green-700' : 'text-red-700'}`}>
              {result.success ? 'Deploy Successful' : 'Deploy Failed'}
            </span>
            {result.duration_s && (
              <span className="text-[10px] text-gray-500 ml-auto flex items-center gap-1">
                <Clock size={10} /> {result.duration_s}s
              </span>
            )}
          </div>
          {result.steps?.map((step, i) => (
            <div key={i} className="flex items-start gap-2 mb-1">
              {step.success
                ? <CheckCircle2 size={10} className="text-green-500 mt-0.5 flex-shrink-0" />
                : <XCircle size={10} className="text-red-500 mt-0.5 flex-shrink-0" />
              }
              <div>
                <span className="text-[10px] font-semibold text-gray-700">{step.step}</span>
                {step.output && <p className="text-[9px] text-gray-500 font-mono break-all">{step.output}</p>}
              </div>
            </div>
          ))}
        </div>
      )}

      {/* Deploy History */}
      {history.length > 0 && (
        <div>
          <p className="text-[10px] font-bold text-gray-500 uppercase tracking-wider mb-2">Recent Deploys</p>
          <div className="bg-white rounded-xl border border-gray-100 divide-y divide-gray-50">
            {history.map((h, i) => (
              <div key={i} className="px-3 py-2 flex items-center gap-3">
                {h.success
                  ? <CheckCircle2 size={12} className="text-green-500" />
                  : <XCircle size={12} className="text-red-500" />
                }
                <span className="text-[10px] text-gray-700 flex-1">
                  {h.timestamp ? new Date(h.timestamp).toLocaleString() : 'Unknown'}
                </span>
                <span className="text-[9px] text-gray-400">{h.duration_s}s</span>
              </div>
            ))}
          </div>
        </div>
      )}
    </div>
  );
}
